import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/blog/shopify-zahlungsarten-deutschland";
const TITLE = "Shopify Zahlungsarten in Deutschland: welche Sie wirklich brauchen";
const DESCRIPTION =
  "Kauf auf Rechnung, PayPal, Klarna, Karte, Apple Pay: welche Zahlarten deutsche Shopify-Shops brauchen, was sie kosten und wie sie den Kaufabschluss beeinflussen.";
const UPDATED = "2026-09-10";

const ANSWER =
  "Für den deutschen Markt decken vier Zahlarten den größten Teil der Nachfrage ab: PayPal, Kauf auf Rechnung, Kreditkarte und Sofortüberweisung oder Lastschrift. Apple Pay und Google Pay lohnen sich zusätzlich, weil sie mobil die Eingabefelder ersparen. Entscheidend ist weniger die Anzahl der Zahlarten als die Frage, ob die im Zielmarkt erwartete Zahlart vorhanden ist und im Checkout sichtbar wird.";

const faqs = [
  {
    q: "Welche Zahlungsarten braucht ein deutscher Shopify-Shop?",
    a: "PayPal, Kreditkarte, Kauf auf Rechnung und eine Banklösung wie Lastschrift oder Sofortüberweisung bilden die Basis. Apple Pay und Google Pay ergänzen das mobil sinnvoll, weil sie den Checkout auf wenige Klicks verkürzen.",
  },
  {
    q: "Lohnt sich Kauf auf Rechnung bei Shopify?",
    a: "In vielen deutschen Sortimenten ja, weil Kundinnen und Kunden ihn gewohnt sind. Anbieter wie Klarna oder Riverty übernehmen dabei das Ausfallrisiko gegen eine höhere Transaktionsgebühr. Ob sich das rechnet, hängt vom Warenkorbwert und der Retourenquote ab.",
  },
  {
    q: "Was kosten Zahlungsarten bei Shopify?",
    a: "Kartenzahlung über Shopify Payments liegt je nach Tarif im niedrigen einstelligen Prozentbereich plus Fixbetrag pro Transaktion. Externe Anbieter können zusätzlich eine Plattformgebühr auslösen, wenn Shopify Payments nicht genutzt wird. Rechnungskauf ist in der Regel die teuerste Zahlart.",
  },
  {
    q: "Wie viele Zahlungsarten sind sinnvoll?",
    a: "Vier bis sechs reichen meist. Eine sehr lange Liste verlangsamt die Entscheidung und macht den Checkout unübersichtlich. Sinnvoller ist es, die zwei meistgenutzten Zahlarten oben zu platzieren.",
  },
];

export const Route = createFileRoute("/blog/shopify-zahlungsarten-deutschland")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Checkout",
      updated: UPDATED,
      breadcrumb: { name: "Blog", path: PATH },
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <ArticlePage
      kicker="Blog · Checkout"
      title="Shopify Zahlungsarten in Deutschland"
      intro="Die Zahlartenauswahl ist der letzte Punkt, an dem ein Kauf noch scheitert. Wer im deutschen Markt verkauft, braucht keine zwanzig Optionen, sondern die vier bis sechs, die Ihre Zielgruppe erwartet, sauber sortiert und ohne Überraschungen im letzten Schritt."
      source="blog-shopify-zahlungsarten"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/blog", label: "Blog" }}
      sections={[
        {
          h: "Die Basis für den deutschen Markt",
          p: [
            "PayPal ist in Deutschland die am breitesten akzeptierte Onlinezahlart und für viele Käufer das Vertrauenssignal überhaupt. Fehlt PayPal, springt ein Teil der Besucher im Checkout ab, ohne die Alternativen zu prüfen.",
            "Kauf auf Rechnung ist die zweite Erwartung, vor allem bei höherpreisiger Ware, Mode und allem mit Retourenwahrscheinlichkeit. Kreditkarte und eine Banklösung wie Lastschrift oder Sofortüberweisung runden die Basis ab.",
          ],
          list: [
            "PayPal, inklusive PayPal Express direkt auf der Produktseite",
            "Kauf auf Rechnung über einen Anbieter mit Risikoübernahme",
            "Kreditkarte über Shopify Payments",
            "Lastschrift oder Sofortüberweisung",
            "Apple Pay und Google Pay für mobile Käufe",
          ],
        },
        {
          h: "Was die Zahlarten kosten",
          p: [
            "Kartenzahlung über Shopify Payments bewegt sich je nach Tarif im niedrigen einstelligen Prozentbereich plus einem festen Betrag je Transaktion. Wer Shopify Payments nicht nutzt, zahlt zusätzlich eine Plattformgebühr auf jede Bestellung, was bei größerem Volumen deutlich ins Gewicht fällt.",
            "Rechnungskauf ist in der Regel die teuerste Option, weil der Anbieter das Ausfallrisiko trägt. Die Rechnung dafür ist einfach: Wenn die zusätzliche Zahlart mehr Deckungsbeitrag erzeugt, als sie an Gebühren kostet, bleibt sie.",
          ],
        },
        {
          h: "Reihenfolge und Darstellung im Checkout",
          p: [
            "Die Reihenfolge der Zahlarten wirkt stärker als die reine Verfügbarkeit. Die zwei meistgenutzten Optionen gehören nach oben, alles Weitere darunter. Logos helfen beim schnellen Erkennen, eine Wand aus Symbolen lenkt dagegen ab.",
            "Genauso wichtig: Zahlarten schon vor dem Checkout zeigen, etwa auf der Produktseite und im Warenkorb. Wer erst im letzten Schritt sieht, dass die gewohnte Zahlart fehlt, bricht ab und kommt selten zurück.",
          ],
        },
        {
          h: "Typische Fehler",
          p: [
            "Häufig sehe ich Shops, die eine Zahlart eingerichtet, aber nie getestet haben. Ein fehlgeschlagener Rücksprung aus dem Zahlungsanbieter kostet direkt Umsatz und fällt in Auswertungen kaum auf, weil die Bestellung nie entsteht.",
            "Ebenso häufig: Zusatzkosten für eine Zahlart, die erst im Checkout auftauchen. Das ist einer der zuverlässigsten Abbruchgründe überhaupt. Kommunizieren Sie Aufschläge früh oder verzichten Sie darauf.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/blog/shopify-checkout-optimieren", label: "Shopify Checkout optimieren" },
        { to: "/ratgeber/shopify-warenkorbabbrueche-reduzieren", label: "Warenkorbabbrüche reduzieren" },
        { to: "/shopify-conversion-optimierung", label: "Shopify Conversion Optimierung" },
      ]}
    />
  );
}
